// View model for the wagering card: what the player's tags resolve to and why. The reason text
// and the tier/override flags come from the API (bs_persist.vip_comp_wagering_tags rules).

const HEADLINES = {
  determined: (w) => ({ headline: `${w.wagering}x`, tone: 'positive' }),
  conflict: () => ({ headline: 'Conflict', tone: 'warning' }),
  not_determined: () => ({ headline: 'Not set', tone: 'muted' }),
};

// matchState: how one matched tag takes part in the result.
const matchState = (status, m) => {
  if (m.overridden) return 'overridden';
  return status === 'conflict' ? 'conflict' : 'applies';
};

// wageringSummary turns check.wagering into the card's headline, reason and one row per matched
// tag, the deciding ones first.
export const wageringSummary = (wagering) => {
  const w = wagering || { status: 'not_determined', matches: [] };
  const head = (HEADLINES[w.status] || HEADLINES.not_determined)(w);
  const rows = (w.matches || [])
    .map((m) => ({ ...m, state: matchState(w.status, m) }))
    .sort((a, b) => (a.state === 'overridden') - (b.state === 'overridden') || a.tier - b.tier);
  return { ...head, reason: w.reason || '', rows };
};

// CC exception tag: same whole-tag rule as the backend (HasCCExceptionTag).
const CC_EXCEPTION_TAG = /^NO more VIP Comp CC exceptions?$/i;
const normalizeTag = (tag) => String(tag).trim().replace(/\s+/g, ' ');

// isCCExceptionTag: the "NO more VIP Comp CC exception(s)" tag, whole tag, any case or spacing.
export const isCCExceptionTag = (tag) => CC_EXCEPTION_TAG.test(normalizeTag(tag));

// TAG_ORDER: wagering tags first (deciding, conflicting, overridden), then CC exception, then the rest.
const TAG_ORDER = { applies: 0, conflict: 1, overridden: 2, cc_exception: 3, other: 4 };

// playerTagViews: every player tag with its role — a wagering tag's state from the summary rows,
// the CC exception tag, or a plain tag — VIP tags first, the original order kept within a role.
export const playerTagViews = (tags, summary) => {
  const stateByTag = new Map((summary?.rows || []).map((r) => [r.tag, r.state]));
  return (tags || [])
    .map((tag, i) => {
      const role = stateByTag.get(tag) || (isCCExceptionTag(tag) ? 'cc_exception' : 'other');
      return { tag, role, i };
    })
    .sort((a, b) => TAG_ORDER[a.role] - TAG_ORDER[b.role] || a.i - b.i)
    .map(({ tag, role }) => ({ tag, role }));
};
